import { inject } from '@angular/core';
import { concatMap, map, type Observable, of } from 'rxjs';

import { PageContextFacade } from '@blaze-runtime/engine-data-access';
import { type DialogClosePayload, type RuntimeEvent } from '@blaze-runtime/engine-domain';
import { type NavigateType, type PageType } from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { sentryLog } from '@blaze-shared/logger-util';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { type PageContext } from '@blaze-shared/projects-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  type BlazeTableSchema,
  type DatasetRow,
  type DatasetSchema,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { type Nullish } from '@blaze-shared/shared-util';
import { type NamespaceId, type NavigateWorkflowNode } from '@blaze-shared/workflow-domain';

import { EngineDialogPages } from '../../../engine-ports/public-api';
import { throwInvalidNodeError } from '../../node-execution-errors';
import { type NodeExecutionInput } from '../../node-execution-input';
import { type NodeExecutionResult } from '../../node-execution-result';
import { NodeExecutionService } from '../../node-execution.service';

import { NavigateActionService } from './navigate-action.service';
import {
  NavigatePageContextFieldService,
  type PageContextFieldData,
} from './navigate-page-context-field.service';
import {
  type NavigateInitResult,
  NavigatePrepareDataService,
} from './navigate-prepare-data.service';
import { evaluateNavigateUrlParameters } from './navigate-url-parameters.utils';

interface PageContextData {
  dataRow: DatasetRow | null;
  dataRowSchema: DatasetSchema | WorkflowSchema;
}

export class NavigateNodeExecutionService extends NodeExecutionService<NavigateWorkflowNode> {
  private readonly engineDialogPages = inject(EngineDialogPages);
  private readonly iss = inject(IntelliSenseService);
  private readonly navigateActionService = inject(NavigateActionService);
  private readonly navigatePageContextFieldService = inject(NavigatePageContextFieldService);
  private readonly navigatePrepareDataService = inject(NavigatePrepareDataService);
  private readonly notificationService = inject(NotificationService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly schemaService = inject(SchemaService);

  protected handle(input: NodeExecutionInput): Observable<NodeExecutionResult | undefined> {
    return this.navigatePrepareDataService
      .prepareData(this.node.navigateType, this.node.target, input.nodeInput, input.schema)
      .pipe(
        concatMap((initResult) =>
          this.navigatePageContextFieldService
            .getPageContextFieldData(
              initResult.inputDataRow,
              initResult.mainDatasetSchema,
              this.node.pageContextField,
            )
            .pipe(
              map((pageContextFieldData) => {
                const { dataRow, dataRowSchema } = getDataRowInfo(initResult, pageContextFieldData);
                const { runtimeContext, target } = this.evaluateContextAndTarget(
                  this.node.target,
                  dataRow,
                  input.runtimeContext,
                );

                return {
                  initResult,
                  dataRow,
                  dataRowSchema,
                  runtimeContext,
                  target,
                };
              }),
            ),
        ),
        concatMap(({ initResult, dataRow, dataRowSchema, runtimeContext, target }) => {
          const { nodeInput, schema, event } = input;
          const { adjustedNavigateType, isInEditor, pageType } = initResult;

          // Validate the evaluated target - stop workflow if target invalid
          const isTargetValid = this.validateEvaluatedTarget(
            target,
            adjustedNavigateType,
            runtimeContext,
          );
          if (!isTargetValid) {
            // Continue workflow
            return of(undefined);
          }

          try {
            this.validateDataRow(dataRow);
            this.validateDataRowSchema(dataRowSchema);
          } catch (error) {
            console.warn(`Navigate node data or schema validation error:`, error);
          }

          // Generate and set page context
          const pageContext = this.preparePageContext(event, this.node, dataRow, dataRowSchema);

          // Log apply
          console.log(`Apply "${this.node.nodeType}":`, {
            node: this.node,
            nodeInput,
            isInEditor,
            schema,
            pageContext,
          });

          // Handle dialog-like pages (opened in a dialog or drawer)
          if (adjustedNavigateType === 'Dialog') {
            if (pageType !== 'Dialog' && pageType !== 'Drawer') {
              throw new Error(
                `Invalid page type for dialog navigation: ${pageType}. Expected 'Dialog' or 'Drawer'.`,
              );
            }

            return this.handleDialogLikePrompt(this.node.target, pageType, pageContext).pipe(
              map((result) => {
                return handleDialogLikePromptResult(result, dataRowSchema, this.node);
              }),
            );
          }

          const urlQueryParams = evaluateNavigateUrlParameters(
            this.node.urlParameters,
            runtimeContext,
            (value) => this.iss.canEvaluate(value),
            (value, context) => this.iss.eval(value, context),
          );

          // Otherwise - navigate to page
          return this.handlePageNavigation(
            target,
            adjustedNavigateType,
            isInEditor,
            pageContext,
            urlQueryParams,
          ).pipe(
            map(() => {
              // Continue to the next node
              return undefined;
            }),
          );
        }),
      );
  }

  protected validate(_input: NodeExecutionInput): void {
    if (!this.node.navigateType || (this.node.navigateType !== 'Logout' && !this.node.target)) {
      throwInvalidNodeError(this.node);
    }
  }

  private validateEvaluatedTarget(
    target: string | undefined,
    adjustedNavigateType: NavigateType,
    runtimeContext: RuntimeContext,
  ): target is string {
    if (adjustedNavigateType === 'Logout') {
      // Logout doesn't require a target
      return true;
    }

    if (target) {
      // If target is defined and not empty, we can proceed
      return true;
    }

    // Otherwise present the error message
    const errorMsg = `Unable to navigate to page. ${this.node.target} evaluates to ${target}`;
    console.error(errorMsg, this.node, runtimeContext);
    this.notificationService.error(errorMsg, true);

    return false;
  }

  private handleDialogLikePrompt(
    dialogId: NamespaceId,
    pageType: PageType,
    pageContext: PageContext | undefined,
  ): Observable<DialogClosePayload> {
    const isDrawer = pageType === 'Drawer';

    return this.engineDialogPages.openDialog(dialogId, pageContext, isDrawer);
  }

  private handlePageNavigation(
    target: string,
    adjustedNavigateType: NavigateType,
    isInEditor: boolean,
    pageContext: PageContext | undefined,
    urlQueryParams: Record<string, string>,
  ): Observable<void> {
    this.navigateActionService.navigate({
      isInEditor,
      navigateType: adjustedNavigateType,
      openInNewTab: this.node.openInNewTab ?? false,
      pageContext,
      target,
      targetProjectId: this.node.projectId,
      urlQueryParams,
    });

    // Move to next node right away
    return of(undefined);
  }

  private evaluateContextAndTarget(
    nodeTarget: string,
    dataRow: DatasetRow | null,
    runtimeContext: RuntimeContext,
  ): { runtimeContext: RuntimeContext; target: string | undefined } {
    let target = this.schemaService.replaceTagIdsWithValuesWhenString(nodeTarget);
    const context = this.iss.updateRuntimeContext(runtimeContext, dataRow);

    // If the content of the target includes a possible variable, we need to evaluate it
    if (this.iss.canEvaluate(nodeTarget)) {
      target = this.iss.eval(nodeTarget, context);
    }

    return { runtimeContext: context, target };
  }

  private preparePageContext(
    event: RuntimeEvent,
    node: NavigateWorkflowNode,
    dataRow: Nullish<DatasetRow>,
    dataRowSchema: BlazeTableSchema | DatasetSchema | WorkflowSchema,
  ): PageContext | undefined {
    // If no data row is present, we can't generate a page context
    if (!dataRow) {
      return;
    }

    const isDataActionEvent = !!event.eventContext?.dataAction;
    // If this navigation doesn't use page context, we can skip this step
    if (!node.passDataAsInput && !node.pageContextField && !isDataActionEvent) {
      return;
    }

    return this.pageContextFacade.generatePageContext({
      data: dataRow,
      schema: dataRowSchema,
      pageId: node.target,
      formContext: undefined,
      isEmptyishRow: false,
    });
  }

  private validateDataRow(datasetRow: DatasetRow | null): asserts datasetRow is DatasetRow {
    if (!datasetRow) {
      sentryLog(`Missing datasetRow`, 'error');
      throw new Error('Missing datasetRow');
    }
  }

  private validateDataRowSchema(
    schema: Nullish<BlazeTableSchema | DatasetSchema | WorkflowSchema>,
  ): asserts schema is BlazeTableSchema | DatasetSchema | WorkflowSchema | null {
    if (schema === undefined) {
      throw new Error('Schema is missing');
    }

    // Schema `null` means no data (undefined)
    if (schema === null) {
      return;
    }

    if (!('id' in schema) && !('workflowId' in schema)) {
      sentryLog(`Invalid schema - missing id and workflowId in dataRowSchema`, 'error', {
        jsonPayload: { schema },
      });
      throw new Error('Invalid schema - missing id and workflowId in dataRowSchema');
    }
  }
}

function getDataRowInfo(
  initData: NavigateInitResult,
  pageContextFieldData: PageContextFieldData | undefined,
): PageContextData {
  if (pageContextFieldData) {
    return {
      dataRow: pageContextFieldData.dataRow,
      dataRowSchema: pageContextFieldData.dataRowSchema,
    };
  }

  return {
    dataRow: initData.inputDataRow,
    dataRowSchema: initData.inputDataRowSchema,
  };
}

/**
 * Handles the result of a dialog-like prompt popup.
 * If the dialog-like prompt was closed without any action, it stops the workflow.
 * Otherwise, it proceeds with the workflow and returns the result.
 */
function handleDialogLikePromptResult(
  result: DialogClosePayload,
  dataRowSchema: DatasetSchema | BlazeTableSchema | WorkflowSchema | null,
  node: NavigateWorkflowNode,
): NodeExecutionResult {
  if (result === undefined) {
    return {
      result: null,
      resultSchema: dataRowSchema,
      requestedNextNodeId: null,
    };
  }

  return {
    result: result.payload,
    resultSchema: dataRowSchema,
    requestedNextNodeId: node.nextNodeId ?? null,
  };
}
